import { Suspense } from 'react';

import Config from '@config';
import {
  ConfirmProvider,
  I18nProvider,
  initI18nInstance,
} from '@gasbi/shared/common/providers';
import { NotificationProvider } from '@gasbi/shared/common/providers/NotificationProvider';
import { ErrorBoundary } from '@gasbi/shared/web/components/ErrorBoundary';
import { KendoLocaleProvider } from '@gasbi/shared/web/providers/KendoLocaleProvider';
import { ThemeProvider } from '@gasbi/shared/web/providers/ThemeProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars';
import 'overlayscrollbars/overlayscrollbars.css';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import * as yup from 'yup';

import { AuthInitProvider } from '@/providers/AuthInitProvider';

import { LicenseInfo } from '@mui/x-license';

import { APP_REFRESH_ID } from './app/constants';
import store from './app/store';
import { i18nInstanceOptions } from './locales';
import { getRoutes } from './route';
import './styles/inter-font.css';

dayjs.locale('uk');

// Yup ships English defaults for every rule that isn't given its own message string ("email is a
// required field") — no form in this app is expected to override every single rule by hand, so a
// global Ukrainian default is set once here instead.
yup.setLocale({
  mixed: {
    default: 'Некоректне значення',
    required: "Обов'язкове поле",
    notNull: 'Поле не може бути порожнім',
    oneOf: 'Оберіть одне з допустимих значень',
    notOneOf: 'Значення недопустиме',
  },
  string: {
    length: 'Повинно містити рівно ${length} символів',
    min: 'Мінімальна довжина — ${min} символів',
    max: 'Максимальна довжина — ${max} символів',
    email: 'Некоректний формат email',
    url: 'Некоректний формат посилання',
    matches: 'Некоректний формат',
  },
  number: {
    min: 'Мінімальне значення — ${min}',
    max: 'Максимальне значення — ${max}',
    lessThan: 'Значення повинно бути менше ${less}',
    moreThan: 'Значення повинно бути більше ${more}',
    positive: 'Значення повинно бути додатнім',
    negative: "Значення повинно бути від'ємним",
    integer: 'Значення повинно бути цілим числом',
  },
  date: {
    min: 'Дата повинна бути не раніше ${min}',
    max: 'Дата повинна бути не пізніше ${max}',
  },
  array: {
    min: 'Мінімальна кількість елементів — ${min}',
    max: 'Максимальна кількість елементів — ${max}',
  },
});

LicenseInfo.setLicenseKey(Config.VITE_MUI_LICENSE);

OverlayScrollbars.plugin(ClickScrollPlugin);

localStorage.setItem(APP_REFRESH_ID, Date.now().toString());

initI18nInstance(i18nInstanceOptions).then(i18n => {
  createRoot(document.getElementById('root')!).render(
    <ErrorBoundary>
      <I18nProvider instance={i18n}>
        <Provider store={store}>
          <ThemeProvider>
            <KendoLocaleProvider>
              <AuthInitProvider>
                <Suspense>
                  <NotificationProvider>
                    <ConfirmProvider>
                      <RouterProvider router={getRoutes()} />
                    </ConfirmProvider>
                  </NotificationProvider>
                </Suspense>
              </AuthInitProvider>
            </KendoLocaleProvider>
          </ThemeProvider>
        </Provider>
      </I18nProvider>
    </ErrorBoundary>,
  );
});


